"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = __importDefault(require("axios"));
const web3_1 = __importDefault(require("web3"));
const ethereumjs_abi_1 = require("ethereumjs-abi");
const bifrost_number_1 = __importDefault(require("@bifrost-platform/bifrost-number"));
const config_1 = __importDefault(require("../config"));
class CallRpc {
    constructor(network, providerConfig) {
        this.network = '';
        this.type = '';
        this.projectID = '';
        this.infuraURL = '';
        this.setNetwork(network);
        this.setProvider(providerConfig);
    }
    setNetwork(network) {
        this.network = network;
        this.setProvider(undefined);
    }
    setProvider(providerConfig) {
        if (!providerConfig) {
            if (!this.type) {
                return;
            }
            providerConfig = {
                type: this.type,
                projectID: this.projectID,
                provider: this.provider
            };
        }
        this.type = providerConfig.type;
        switch (this.type) {
            case 'infura':
                if (!providerConfig.projectID) {
                    throw new Error('Require project ID to using infura');
                }
                this.projectID = providerConfig.projectID;
                this.infuraURL = CallRpc.getInfuraUrl(this.network, this.projectID);
                this.web3 = new web3_1.default(this.infuraURL);
                break;
            case 'wallet':
                if (!providerConfig.provider) {
                    throw new Error('Require provider to using wallet');
                }
                this.provider = providerConfig.provider;
                this.web3 = new web3_1.default(providerConfig.provider);
                this.infuraURL = '';
                this.callRequestAccounts(this.network);
                break;
            default:
                throw new Error('Require valid provider type');
        }
    }
    getNetwork() {
        return this.network;
    }
    getType() {
        return this.type;
    }
    getProvider() {
        return this.provider;
    }
    getWeb3() {
        return this.web3;
    }
    call(startNetwork, userAddress, entryPointAddress, method, args) {
        return new Promise(async (resolve, revert) => {
            try {
                const encodedData = ethereumjs_abi_1.simpleEncode(method.signature, ...(args || []));
                const data = CallRpc.bufferToHex(encodedData);
                const params = [
                    {
                        from: userAddress,
                        to: entryPointAddress,
                        data,
                        value: '0x0'
                    },
                    'latest'
                ];
                const rawResponse = await this.rawCall(startNetwork, userAddress, 'eth_call', params);
                if (startNetwork !== this.network) {
                    throw config_1.default.code.diffNetwork;
                }
                if (!rawResponse || `${rawResponse}`.length < 2 || rawResponse == '0x') {
                    throw config_1.default.code.noResponse;
                }
                resolve(this.web3.eth.abi.decodeParameters(method.outputTypes, rawResponse));
            }
            catch (error) {
                if (error !== config_1.default.code.diffNetwork && error !== config_1.default.code.noResponse) {
                    console.log(method.signature, error);
                }
                revert(error);
            }
        });
    }
    rawCall(startNetwork, from = config_1.default.default.address.call, method = 'eth_call', params) {
        return new Promise(async (resolve, reject) => {
            try {
                let rawResponse;
                switch (this.type) {
                    case 'infura':
                        rawResponse = await this.httpCall(startNetwork, from, method, params);
                        break;
                    case 'wallet':
                        rawResponse = await this.providerCall(startNetwork, from, method, params);
                        break;
                    default:
                        reject(new Error('Require valid call rpc type'));
                        break;
                }
                resolve(rawResponse);
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async providerCall(startNetwork, from = config_1.default.default.address.call, method = 'eth_call', params = []) {
        return new Promise(async (resolve, reject) => {
            try {
                if (startNetwork !== this.network) {
                    throw config_1.default.code.diffNetwork;
                }
                const requestObject = {
                    method,
                    params,
                    from
                };
                let response = [];
                if (!!this.provider
                    && typeof this.provider === 'object'
                    && typeof this.provider.request === 'function') {
                    response = await this.provider.request(requestObject);
                }
                resolve(response);
            }
            catch (error) {
                reject(error);
            }
        });
    }
    httpCall(startNetwork, from = config_1.default.default.address.call, method = 'eth_call', params = []) {
        return new Promise(async (resolve, reject) => {
            try {
                if (startNetwork !== this.network) {
                    throw config_1.default.code.diffNetwork;
                }
                const response = await axios_1.default.post(this.infuraURL, {
                    id: 1,
                    jsonrpc: '2.0',
                    method,
                    params,
                    from
                });
                if (response.data.result) {
                    resolve(response.data.result);
                }
                else {
                    if (response.data.error) {
                        reject(response.data.error);
                    }
                    else {
                        reject(new Error('Unknown error'));
                    }
                }
            }
            catch (error) {
                reject(error);
            }
        });
    }
    callNetwork(startNetwork) {
        return new Promise(async (resolve) => {
            let result = '';
            try {
                result = await this.rawCall(startNetwork, '', 'net_version', []) || '';
            }
            catch (error) {
            }
            resolve(result);
        });
    }
    callBlockNumber(startNetwork) {
        return new Promise(async (resolve) => {
            let result = 0;
            try {
                result = parseInt(await this.rawCall(startNetwork, '', 'eth_blockNumber', []) || 0) || 0;
            }
            catch (error) {
            }
            resolve(result);
        });
    }
    callGasPrice(startNetwork) {
        return new Promise(async (resolve) => {
            let result = '0';
            try {
                result = new bifrost_number_1.default(await this.rawCall(startNetwork, '', 'eth_gasPrice', [])).toStr();
            }
            catch (error) {
            }
            resolve(result);
        });
    }
    callRequestAccounts(startNetwork) {
        return new Promise(async (resolve) => {
            let result = [];
            if (this.type === 'wallet') {
                try {
                    result = await this.rawCall(startNetwork, '', 'eth_requestAccounts', []) || [];
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callAccounts(startNetwork) {
        return new Promise(async (resolve) => {
            let result = [];
            if (this.type === 'wallet') {
                try {
                    result = await this.rawCall(startNetwork, '', 'eth_accounts', []) || [];
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callTransactionReceipt(startNetwork, transactionHash) {
        return new Promise(async (resolve) => {
            let result;
            if (this.type === 'wallet') {
                try {
                    result = await this.rawCall(startNetwork, '', 'eth_getTransactionReceipt', [transactionHash]);
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callBalance(startNetwork, address) {
        return new Promise(async (resolve) => {
            let result = '0';
            if (this.type === 'wallet') {
                try {
                    if (!address || !web3_1.default.utils.isAddress(address)) {
                        address = (await this.callAccounts(startNetwork))[0] || '';
                    }
                    const params = [
                        address,
                        'latest'
                    ];
                    result = new bifrost_number_1.default(await this.rawCall(startNetwork, address, 'eth_getBalance', params)).div(new bifrost_number_1.default('10').pow(config_1.default.default.decimals)).toStr();
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callErc20Balance(startNetwork, tokenAddress, userAddress, decimals) {
        return new Promise(async (resolve) => {
            let result = '0';
            if (this.type === 'wallet'
                && tokenAddress
                && web3_1.default.utils.isAddress(tokenAddress)) {
                try {
                    if (!userAddress || !web3_1.default.utils.isAddress(userAddress)) {
                        userAddress = (await this.callAccounts(startNetwork))[0] || '';
                    }
                    const encodedData = ethereumjs_abi_1.simpleEncode('balanceOf(address)', userAddress);
                    const data = CallRpc.bufferToHex(encodedData);
                    const params = [
                        {
                            from: userAddress,
                            to: tokenAddress,
                            data,
                            value: '0x0'
                        },
                        'latest'
                    ];
                    const response = this.web3.eth.abi.decodeParameters(['(uint256)'], await this.rawCall(startNetwork, userAddress, 'eth_call', params));
                    result = ((response || [])[0] || [])[0];
                    result = new bifrost_number_1.default(result).div(new bifrost_number_1.default('10').pow(decimals || config_1.default.default.decimals)).toStr();
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callErc20Allowance(startNetwork, tokenAddress, userAddress, targetAddress, decimals) {
        return new Promise(async (resolve) => {
            let result = '0';
            if (this.type === 'wallet'
                && tokenAddress
                && targetAddress
                && web3_1.default.utils.isAddress(tokenAddress)
                && web3_1.default.utils.isAddress(targetAddress)) {
                try {
                    if (!userAddress || !web3_1.default.utils.isAddress(userAddress)) {
                        userAddress = (await this.callAccounts(startNetwork))[0] || '';
                    }
                    const encodedData = ethereumjs_abi_1.simpleEncode('allowance(address,address)', userAddress, targetAddress);
                    const data = CallRpc.bufferToHex(encodedData);
                    const params = [
                        {
                            from: userAddress,
                            to: tokenAddress,
                            data,
                            value: '0x0'
                        },
                        'latest'
                    ];
                    const response = this.web3.eth.abi.decodeParameters(['(uint256)'], await this.rawCall(startNetwork, userAddress, 'eth_call', params));
                    result = ((response || [])[0] || [])[0];
                    result = new bifrost_number_1.default(result).div(new bifrost_number_1.default('10').pow(decimals || config_1.default.default.decimals)).toStr();
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    callErc20Decimals(startNetwork, tokenAddress) {
        return new Promise(async (resolve) => {
            let result = '0';
            if (this.type === 'wallet'
                && tokenAddress
                && web3_1.default.utils.isAddress(tokenAddress)) {
                try {
                    const encodedData = ethereumjs_abi_1.simpleEncode('decimals()');
                    const data = CallRpc.bufferToHex(encodedData);
                    const params = [
                        {
                            from: config_1.default.default.address.call,
                            to: tokenAddress,
                            data,
                            value: '0x0'
                        },
                        'latest'
                    ];
                    const response = this.web3.eth.abi.decodeParameters(['(uint8)'], await this.rawCall(startNetwork, '', 'eth_call', params));
                    result = ((response || [])[0] || [])[0];
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    watchAsset(startNetwork, address, symbol, decimals = 18, image = '', type = 'ERC20') {
        return new Promise(async (resolve) => {
            let result = false;
            if (this.type === 'wallet'
                && address
                && symbol
                && typeof decimals === 'number'
                && web3_1.default.utils.isAddress(address)) {
                try {
                    result = await this.rawCall(startNetwork, '', 'wallet_watchAsset', {
                        type,
                        options: {
                            address,
                            symbol,
                            decimals,
                            image
                        }
                    });
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    addChain(startNetwork, chainId, chainName, rpcUrls = [], blockExplorerUrls, nativeCurrency, iconUrls) {
        return new Promise(async (resolve) => {
            let result = false;
            if (this.type === 'wallet'
                && chainId
                && rpcUrls
                && typeof rpcUrls === 'object'
                && rpcUrls.length) {
                try {
                    await this.rawCall(startNetwork, '', 'wallet_addEthereumChain', [{
                            chainId,
                            chainName,
                            rpcUrls,
                            blockExplorerUrls,
                            nativeCurrency,
                            iconUrls
                        }]);
                    result = true;
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    switchChain(startNetwork, chainId) {
        return new Promise(async (resolve) => {
            let result = false;
            if (this.type === 'wallet'
                && chainId) {
                try {
                    await this.rawCall(startNetwork, '', 'wallet_switchEthereumChain', [{
                            chainId
                        }]);
                    result = true;
                }
                catch (error) {
                }
            }
            resolve(result);
        });
    }
    async switchAndAddChain(startNetwork, chainId, chainName, rpcUrls = [], blockExplorerUrls, nativeCurrency, iconUrls) {
        let result = false;
        result = await this.switchChain(startNetwork, chainId);
        if (!result) {
            result = await this.addChain(startNetwork, chainId, chainName, rpcUrls, blockExplorerUrls, nativeCurrency, iconUrls);
        }
        return result;
    }
    static getInfuraUrl(network, projectID) {
        if (!network) {
            throw new Error('Require infura network');
        }
        else if (!projectID) {
            throw new Error('Require infura project ID');
        }
        let path = '';
        switch (`${network}`.toLowerCase()) {
            case 'mainnet':
            case 'ropsten':
            case 'rinkeby':
            case 'kovan':
            case 'goerli':
                path = `${config_1.default.infura.host}${config_1.default.infura.path.network || ''}`;
                break;
            case 'sepolia':
                path = `${config_1.default.infura.sepolia || ''}`;
                break;
            case 'base-sepolia':
                path = `${config_1.default.infura.sepoliaBase || ''}`;
                break;
            case 'arbitrum-sepolia':
                path = `${config_1.default.infura.sepoliaArbitrum || ''}`;
                break;
            case 'bsc':
                path = `${config_1.default.infura.bsc || ''}`;
                break;
            case 'bsc-test':
                path = `${config_1.default.infura.bscTest || ''}`;
                break;
            case 'avalanche':
                path = `${config_1.default.infura.avalanche || ''}`;
                break;
            case 'avalanche-test':
                path = `${config_1.default.infura.avalancheTest || ''}`;
                break;
            case 'kaia':
                path = `${config_1.default.infura.kaia || ''}`;
                break;
            case 'kaia-test':
                path = `${config_1.default.infura.kaiaTest || ''}`;
                break;
            case 'bifrost':
                path = `${config_1.default.infura.bifrost || ''}`;
                break;
            case 'bifrost-test':
                path = `${config_1.default.infura.bifrostTest || ''}`;
                break;
            case 'pilab-poa2':
                path = `${config_1.default.infura.pilabPoa2 || ''}`;
                break;
            case 'pilab-fork':
                path = `${config_1.default.infura.pilabFork || ''}`;
                break;
            case 'ipfs':
                path = `${config_1.default.infura.host}${config_1.default.infura.path.ipfs || ''}`;
                break;
            default:
                throw new Error('Require valid infura network');
        }
        return path.replace('{prefix}', network).replace('{projectID}', projectID);
    }
    static bufferToHex(buffer) {
        return `0x${Array.prototype.map.call(new Uint8Array(buffer), (x) => (`00${x.toString(16)}`).slice(-2)).join('')
            || '0'}`;
    }
}
exports.default = CallRpc;
;
//# sourceMappingURL=CallRpc.js.map